import React, { forwardRef, useEffect, useState, useMemo, useImperativeHandle } from 'react';
import { usePopper } from 'react-popper';
import { PopoverContainer } from './styled';
import PopoverStylesContext from './context';
import { useClickAway } from 'react-use';
import { Portal } from '../portal';
const POSITIONS = ['top', 'bottom', 'left', 'right'];
export const Popover = forwardRef(function Popover({ children, anchorEl, isOpen, modifiers = [], positions = POSITIONS, onClickOutside, strategy = 'fixed', disablePortal = false, container, ...props }, ref) {
    const [popperElement, setPopperElement] = useState(null);
    useClickAway({ current: popperElement }, (event) => {
        const isTriggeredAnchorEl = anchorEl && anchorEl.contains(event.target);
        if (onClickOutside && !isTriggeredAnchorEl) {
            onClickOutside(event);
        }
    }, ['mousedown', 'touchstart']);
    const [placement, ...fallbackPlacements] = useMemo(() => {
        if (Array.isArray(positions) && positions.length === 0) {
            return POSITIONS;
        }
        return positions;
    }, [positions]);
    const popperModifiers = useMemo(() => {
        return [
            {
                name: 'preventOverflow',
                enabled: true,
                options: {
                    rootBoundary: 'document',
                    altBoundary: disablePortal
                }
            },
            {
                name: 'flip',
                enabled: Boolean(fallbackPlacements && fallbackPlacements.length),
                options: { fallbackPlacements, rootBoundary: 'document', altBoundary: disablePortal }
            },
            ...modifiers
        ];
    }, [modifiers, fallbackPlacements]);
    const { styles, attributes, update, forceUpdate } = usePopper(anchorEl, popperElement, {
        modifiers: popperModifiers,
        strategy,
        placement
    });
    useImperativeHandle(ref, 
    /* @ts-ignore */
    () => {
        if (!update && !forceUpdate)
            return {};
        return {
            update() {
                update === null || update === void 0 ? void 0 : update();
            },
            forceUpdate() {
                forceUpdate === null || forceUpdate === void 0 ? void 0 : forceUpdate();
            }
        };
    }, [update, forceUpdate]);
    useEffect(() => {
        if (!update) {
            return;
        }
        if (isOpen) {
            popperElement === null || popperElement === void 0 ? void 0 : popperElement.setAttribute('data-popper-reference-hidden', 'false');
            update();
        }
        else {
            popperElement === null || popperElement === void 0 ? void 0 : popperElement.removeAttribute('data-popper-reference-hidden');
        }
    }, [isOpen, update]);
    if (!isOpen)
        return null;
    return (React.createElement(PopoverStylesContext.Provider, { value: styles },
        React.createElement(Portal, { disablePortal: disablePortal, container: container },
            React.createElement(PopoverContainer, { ref: setPopperElement, style: {
                    position: 'fixed',
                    // Fix Popper.js display issue
                    top: 0,
                    left: 0,
                    ...styles.popper
                }, ...props, ...attributes.popper }, children))));
});
